import {
  Button,
  DateInput,
  FormContainer,
  FormInput,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Grid, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import FormTextEditor from "../../../../components/UI/TextEditor/FormTextEditor";
import {
  IEditEsignEmailSendRule,
  INewEsignEmailSendRule,
} from "../../../../models/esign-email-send-rule";
import {
  createEsignEmailSendRule,
  getEsignEmailSendRuleById,
  updateEsignEmailSendRuleById,
} from "../../../../services/esignEmailSendRule.service";
import { dateOnlyStringToNewDate } from "../../../../utils/conversions";
import { AvailableVariablesModal } from "./AvailableVariablesModal";
import { emailSendRuleSchema, TEmailSendRuleForm } from "./emailSendRuleSchema";

const translationPath = "4uESign.page.automations.emailSendRules.";

const getBadRequestMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    throw error;
  }

  const responseData = error.response.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (responseData && typeof responseData === "object") {
    const data = responseData as {
      detail?: string;
      message?: string;
      errors?: Record<string, string[]>;
    };

    const validationErrors = data.errors
      ? Object.values(data.errors).flat().join(" | ")
      : "";

    return data.detail || data.message || validationErrors || fallback;
  }

  return fallback;
};

const EmailSendRuleForm = () => {
  const { t } = useTranslation();
  const { eI } = useParams();
  const [variablesOpen, setVariablesOpen] = useState(false);

  const createRequest = useCallback(
    async (item: INewEsignEmailSendRule) => {
      try {
        return await createEsignEmailSendRule(item);
      } catch (error) {
        throw new Error(
          getBadRequestMessage(error, t(translationPath + "overlap_error")),
        );
      }
    },
    [t],
  );

  const updateRequest = useCallback(
    async ({ item, id }: { item: IEditEsignEmailSendRule; id: string }) => {
      try {
        return await updateEsignEmailSendRuleById({ item, id });
      } catch (error) {
        throw new Error(
          getBadRequestMessage(error, t(translationPath + "overlap_error")),
        );
      }
    },
    [t],
  );

  const { sendRequest: save, loading: saveLoading } = useFetch(createRequest);
  const { sendRequest: update, loading: updateLoading } =
    useFetch(updateRequest);
  const { sendRequest: get, loading: getLoading } = useFetch(
    getEsignEmailSendRuleById,
  );

  const toPayload = useCallback(
    (value: TEmailSendRuleForm): INewEsignEmailSendRule => ({
      name: value.name,
      initialDate: format(value.initialDate, "yyyy-MM-dd"),
      endDate: format(value.endDate, "yyyy-MM-dd"),
      subject: value.subject,
      body: value.body,
      enable: value.enable,
    }),
    [],
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TEmailSendRuleForm>> => {
      const { data, success } = await get(id);

      if (!data) return { data, success };

      return {
        success: true,
        data: {
          ...data,
          initialDate: dateOnlyStringToNewDate(
            data.initialDate.substring(0, 10),
          ),
          endDate: dateOnlyStringToNewDate(data.endDate.substring(0, 10)),
        },
      };
    },
    [get],
  );

  return (
    <>
      <FormContainer<TEmailSendRuleForm>
        saveFunction={(value) => save(toPayload(value))}
        updateFunction={(value, id) =>
          update({ item: { ...toPayload(value), id }, id })
        }
        getFunction={getHandler}
        defaultValues={{ enable: true }}
        loading={saveLoading || updateLoading || getLoading}
        title={t(translationPath + "form_title")}
        subtitle={t(translationPath + "new")}
        subtitleWatchField="name"
        schema={emailSendRuleSchema}
        backUrl={`/e/${eI}/4uESign/createAutomations/emailSendRules`}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} textAlign="end">
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormInput
                name="name"
                label={t(translationPath + "name")}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateInput
                name="initialDate"
                label={t(translationPath + "initial_date")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateInput
                name="endDate"
                label={t(translationPath + "end_date")}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
              >
                <Box flex={1}>
                  <FormInput
                    name="subject"
                    label={t(translationPath + "subject")}
                    size="small"
                    fullWidth
                  />
                </Box>

                <Tooltip title={t(translationPath + "variables_tooltip")}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                    onClick={() => setVariablesOpen(true)}
                    sx={{ height: 40, whiteSpace: "nowrap" }}
                  >
                    {t(translationPath + "variables_button")}
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(translationPath + "body")}
              </Typography>
              <FormTextEditor name="body" />
            </Grid>
          </Grid>
        )}
      />

      <AvailableVariablesModal
        open={variablesOpen}
        onClose={() => setVariablesOpen(false)}
      />
    </>
  );
};

export default EmailSendRuleForm;
